import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { saveClientProfile, getClientProfile, saveClientToken } from "@/lib/clientStore";
import useConsultantPWAManifest from "@/lib/useConsultantPWAManifest";
import api, { fileUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Loader2, ChevronLeft, Shield, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ClientOnboarding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const existing = getClientProfile();
  const isFirstTime = !existing;
  const [consultant, setConsultant] = useState(null);
  const [form, setForm] = useState(existing || {
    first_name: "", last_name: "", email: "", phone: "", vehicle_plate: "",
    address_street: "", address_city: "", address_postal_code: "", address_province: "",
    fiscal_code: "",
    gdpr_consent: false, marketing_consent: false, marketing_totalklick_consent: false,
  });
  const [loading, setLoading] = useState(false);

  // Active consultant PWA manifest on this page too (in case the install prompt fires here)
  useConsultantPWAManifest(slug, consultant);

  useEffect(() => {
    api.get(`/consultants/by-slug/${slug}`).then(({ data }) => setConsultant(data)).catch(() => {});
  }, [slug]);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (isFirstTime && !form.gdpr_consent) {
      toast.error("Devi accettare il trattamento dati per proseguire");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register/client", { ...form, consultant_slug: slug });
      saveClientProfile({ ...form, user_id: data.user.id });
      saveClientToken(data.token);
      toast.success(isFirstTime ? "Registrazione completata" : "Profilo aggiornato");
      navigate(`/c/${slug}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = "Errore registrazione";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) {
        // Pydantic validation errors → friendly Italian message
        const missing = detail.map((e) => e.loc?.[e.loc.length - 1]).filter(Boolean);
        msg = `Compila tutti i campi obbligatori: ${missing.join(", ")}`;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = consultant?.brand_color_primary || "#F77F2A";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#0F172A] text-white safe-top">
        <div className="max-w-md mx-auto px-5 py-4 flex items-center gap-3">
          {!isFirstTime && (
            <button onClick={() => navigate(`/c/${slug}`)} data-testid="onboarding-back-button"
              className="p-2 -ml-2 hover:bg-white/10 rounded-lg text-white/80">
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          <div className="flex-1 min-w-0 text-center">
            {consultant?.logo_url ? (
              <img src={fileUrl(consultant.logo_url)} alt={consultant.studio_name}
                className="h-10 mx-auto bg-white rounded-lg p-1 object-contain" style={{ maxWidth: 160 }} />
            ) : consultant?.studio_name ? (
              <div
                className="h-10 mx-auto px-3 rounded-lg flex items-center justify-center font-black text-white"
                style={{ background: consultant.brand_color_primary || "#2456C6", maxWidth: 240 }}
                data-testid="onboarding-studio-fallback"
              >
                {consultant.studio_name}
              </div>
            ) : (
              <Logo className="h-10 mx-auto" />
            )}
            {/* Subtitle: hide when the fallback box already shows the studio_name */}
            {consultant && consultant.logo_url && (
              <div className="text-xs text-white/70 mt-1 truncate">{consultant.studio_name}</div>
            )}
          </div>
          <div className="w-9" />
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 py-6">
        <h1 className="text-2xl sm:text-3xl font-black text-[#1E293B] mb-2">
          {isFirstTime ? "Registrati come cliente" : "Aggiorna il tuo profilo"}
        </h1>
        <p className="text-slate-600 mb-6 leading-relaxed text-sm">
          {isFirstTime
            ? <>Compila i tuoi dati una sola volta. Resterai collegato sul telefono per le prossime visite — in caso di sinistro apri la pratica in 10 secondi.</>
            : <>Modifica i tuoi dati personali e di contatto. I cambiamenti saranno usati nelle prossime pratiche.</>}
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div className="card-gs !p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Dati personali</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-gs">Nome</label>
                <input data-testid="onb-firstname-input" required type="text" value={form.first_name}
                  onChange={(e) => update("first_name", e.target.value)} className="input-gs" />
              </div>
              <div>
                <label className="label-gs">Cognome</label>
                <input data-testid="onb-lastname-input" required type="text" value={form.last_name}
                  onChange={(e) => update("last_name", e.target.value)} className="input-gs" />
              </div>
            </div>
            <div className="mt-3">
              <label className="label-gs">Codice fiscale <span className="text-red-500">*</span></label>
              <input data-testid="onb-cf-input" required type="text" value={form.fiscal_code}
                onChange={(e) => update("fiscal_code", e.target.value.toUpperCase())}
                className="input-gs uppercase" maxLength={16} minLength={11} placeholder="RSSMRA80A01H501U" />
            </div>
          </div>

          <div className="card-gs !p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Recapiti</div>
            <div>
              <label className="label-gs">Email</label>
              <input data-testid="onb-email-input" required type="email" value={form.email}
                onChange={(e) => update("email", e.target.value)} className="input-gs" autoComplete="email"
                readOnly={!isFirstTime} />
              <div className="text-xs text-slate-500 mt-1">Riceverai qui OTP firma e conferma pratica{!isFirstTime && " · L'email non si modifica"}</div>
            </div>
            <div className="mt-3">
              <label className="label-gs">Telefono</label>
              <input data-testid="onb-phone-input" required type="tel" value={form.phone}
                onChange={(e) => update("phone", e.target.value)} className="input-gs" autoComplete="tel"
                placeholder="+39 …" />
            </div>
          </div>

          <div className="card-gs !p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Indirizzo</div>
            <div>
              <label className="label-gs">Via e numero civico <span className="text-red-500">*</span></label>
              <input data-testid="onb-street-input" required type="text" value={form.address_street}
                onChange={(e) => update("address_street", e.target.value)} className="input-gs"
                placeholder="Via Roma, 12" />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <div>
                <label className="label-gs">CAP <span className="text-red-500">*</span></label>
                <input data-testid="onb-cap-input" required type="text" value={form.address_postal_code}
                  onChange={(e) => update("address_postal_code", e.target.value)} className="input-gs"
                  maxLength={5} minLength={5} pattern="[0-9]{5}" placeholder="00100" />
              </div>
              <div className="col-span-2">
                <label className="label-gs">Città <span className="text-red-500">*</span></label>
                <input data-testid="onb-city-input" required type="text" value={form.address_city}
                  onChange={(e) => update("address_city", e.target.value)} className="input-gs"
                  placeholder="Roma" />
              </div>
            </div>
            <div className="mt-3">
              <label className="label-gs">Provincia <span className="text-red-500">*</span></label>
              <input data-testid="onb-province-input" required type="text" value={form.address_province}
                onChange={(e) => update("address_province", e.target.value.toUpperCase())}
                className="input-gs uppercase" maxLength={2} minLength={2} placeholder="RM" />
            </div>
          </div>

          <div className="card-gs !p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Veicolo</div>
            <label className="label-gs">Targa veicolo principale</label>
            <input data-testid="onb-plate-input" required type="text" value={form.vehicle_plate}
              onChange={(e) => update("vehicle_plate", e.target.value.toUpperCase())} className="input-gs uppercase"
              maxLength={10} placeholder="AB123CD" />
            <div className="text-xs text-slate-500 mt-1">Potrai sempre modificarla pratica per pratica</div>
          </div>

          <div className="card-gs !p-4 bg-slate-50 border-slate-200">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-[#2456C6]" />
              <div className="text-sm font-bold text-[#1E293B]">Consensi</div>
            </div>
            {isFirstTime && (
              <label className="flex items-start gap-3 cursor-pointer mb-3">
                <input data-testid="onb-gdpr-checkbox" type="checkbox" checked={form.gdpr_consent}
                  onChange={(e) => update("gdpr_consent", e.target.checked)}
                  className="mt-1 w-5 h-5 accent-[#F77F2A] flex-shrink-0" required />
                <span className="text-sm text-slate-700 leading-snug">
                  Acconsento al trattamento dei miei dati personali ai sensi del <b>Reg. UE 679/2016 (GDPR)</b> per la gestione di pratiche di sinistro e per la firma elettronica delle pratiche tramite OTP/email (FES eIDAS). Ho letto la <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#2456C6] font-bold underline">Privacy Policy</a>. <span className="text-red-600">Obbligatorio</span>
                </span>
              </label>
            )}
            <label className="flex items-start gap-3 cursor-pointer mb-3">
              <input data-testid="onb-marketing-checkbox" type="checkbox" checked={form.marketing_consent}
                onChange={(e) => update("marketing_consent", e.target.checked)}
                className="mt-1 w-5 h-5 accent-[#2456C6] flex-shrink-0" />
              <span className="text-sm text-slate-700 leading-snug">
                Acconsento a ricevere comunicazioni di servizio dal consulente {consultant?.studio_name ? <b>{consultant.studio_name}</b> : null}. <span className="text-slate-400">(Facoltativo)</span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input data-testid="onb-totalklick-checkbox" type="checkbox" checked={form.marketing_totalklick_consent}
                onChange={(e) => update("marketing_totalklick_consent", e.target.checked)}
                className="mt-1 w-5 h-5 accent-[#F77F2A] flex-shrink-0" />
              <span className="text-sm text-slate-700 leading-snug">
                Acconsento a ricevere informazioni e comunicazioni commerciali da <b>TotalKlick srls</b> (titolare di GoStreet) anche relative ai suoi prodotti e partner. I miei dati potranno essere usati per attività di marketing profilato. <span className="text-slate-400">(Facoltativo, revocabile in qualsiasi momento)</span>
              </span>
            </label>
          </div>

          <button data-testid="onb-submit-button" type="submit" disabled={loading || (isFirstTime && !form.gdpr_consent)}
            className="w-full text-white font-black text-lg rounded-2xl py-4 transition-all active:scale-[0.98] flex items-center justify-center gap-3 shadow-lg disabled:opacity-50"
            style={{ background: primaryColor }}>
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>
              <CheckCircle2 className="w-5 h-5" /> {isFirstTime ? "Registrati e prosegui" : "Salva modifiche"}
            </>)}
          </button>
        </form>
      </main>
    </div>
  );
}
